import React from 'react';
import { Building2, UserCheck } from 'lucide-react';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';

interface AccountTypePickerProps {
  value: 'STAFF' | 'CANDIDATE';
  onChange: (next: 'STAFF' | 'CANDIDATE') => void;
  className?: string;
}

/**
 * Signup-account-type picker: Organization / Creator (STAFF, owns an org) vs
 * Candidate / Participant (CANDIDATE, portal). Only consumed for brand-new tokenless
 * signups — a join token forces CANDIDATE server-side, and returning users are never re-provisioned.
 */
export const AccountTypePicker: React.FC<AccountTypePickerProps> = ({
  value,
  onChange,
  className,
}) => {
  const { t } = useTranslation();

  const options = [
    {
      value: 'CANDIDATE' as const,
      label: t('auth.account_type_student'),
      desc: t('auth.account_type_student_desc'),
      Icon: UserCheck,
    },
    {
      value: 'STAFF' as const,
      label: t('auth.account_type_teacher'),
      desc: t('auth.account_type_teacher_desc'),
      Icon: Building2,
    },
  ];

  return (
    <div className={cn('space-y-2', className)}>
      <p className="text-sm font-medium text-foreground">{t('auth.account_type_question')}</p>
      <div className="grid grid-cols-2 gap-3">
        {options.map(({ value: optionValue, label, desc, Icon }) => {
          const selected = value === optionValue;
          return (
            <button
              key={optionValue}
              type="button"
              onClick={() => onChange(optionValue)}
              aria-pressed={selected}
              className={cn(
                'flex flex-col items-start gap-1 rounded-xl border p-3.5 text-left transition-all duration-150',
                selected
                  ? 'border-foreground ring-1 ring-foreground'
                  : 'border-border/80 hover:bg-accent/40 hover:border-foreground/20',
              )}
            >
              <Icon
                className={cn('h-5 w-5', selected ? 'text-foreground' : 'text-muted-foreground')}
                aria-hidden
              />
              <span className="text-sm font-semibold text-foreground">{label}</span>
              <span className="text-xs text-muted-foreground leading-snug">{desc}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AccountTypePicker;